/**
 * BarChart Component
 *
 * Displays categorical or time-series data as vertical bars using Recharts.
 * Supports single or multi-dimensional metrics with responsive design.
 *
 * Performance: Uses ChartDataContext for cached data - no individual API calls
 */

import PropTypes from 'prop-types';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useChartData } from '../../contexts/ChartDataContext';
import { formatDate, formatMetricValue, formatAxisValue } from '../../utils/formatters';
import './ChartStyles.css';

const BarChart = ({ chartId }) => {
  // Get chart data from cache (loaded via batch request in template)
  const { getChartData: getCachedChartData } = useChartData();
  const cachedData = getCachedChartData(chartId);

  const { data, loading, error } = cachedData;

  if (loading) {
    return (
      <div className="chart-container loading">
        <div className="spinner"></div>
        <p>Loading chart...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container error">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!data || !data.data_points || data.data_points.length === 0) {
    return (
      <div className="chart-container no-data">
        <p>No data available</p>
      </div>
    );
  }

  // Extract chart configuration
  const {
    chart_name,
    metric_name,
    metric_unit,
    x_label,
    y_label,
    data_points,
    dimensions,
  } = data;

  // Custom tooltip formatter
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length > 0) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{formatDate(label, 'short')}</p>
          {payload.map((entry, index) => (
            <p key={index} className="tooltip-value" style={{ color: entry.color }}>
              {entry.name}: {formatMetricValue(entry.value, metric_unit)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Format X-axis labels
  const formatXAxis = (dateStr) => {
    if (!dateStr) return '';

    // Try to parse as date
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      // It's a valid date
      const firstDate = new Date(data_points[0]?.date);
      const lastDate = new Date(data_points[data_points.length - 1]?.date);
      const timeSpanDays = (lastDate - firstDate) / (1000 * 60 * 60 * 24);

      if (timeSpanDays <= 90) {
        return formatDate(dateStr, 'short');
      } else if (timeSpanDays <= 730) {
        return formatDate(dateStr, 'month');
      } else {
        return formatDate(dateStr, 'year');
      }
    }

    // Not a date, return as-is (for categorical data)
    return String(dateStr).length > 15 ? String(dateStr).substring(0, 12) + '...' : dateStr;
  };

  // Prepare data for Recharts
  let chartData;
  let barKeys;

  if (dimensions && dimensions.length > 0) {
    // Multi-dimensional: pivot data by dimension
    const pivotedData = {};

    data_points.forEach(point => {
      const date = point.date;
      const dimension = point.dimension || 'Unknown';
      const value = point.value;

      if (!pivotedData[date]) {
        pivotedData[date] = { date };
      }
      pivotedData[date][dimension] = value;
    });

    chartData = Object.values(pivotedData).sort((a, b) =>
      new Date(a.date) - new Date(b.date)
    );
    barKeys = dimensions;
  } else {
    // Single series
    chartData = data_points.map(point => ({
      date: point.date,
      value: point.value,
    }));
    barKeys = ['value'];
  }

  // Color palette for multiple bars
  const colors = [
    '#3498db', // Blue
    '#e74c3c', // Red
    '#2ecc71', // Green
    '#f39c12', // Orange
    '#9b59b6', // Purple
    '#1abc9c', // Teal
    '#34495e', // Dark gray
    '#e67e22', // Dark orange
  ];

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3 className="chart-title">{chart_name || metric_name}</h3>
        <p className="chart-subtitle">{y_label || metric_name}</p>
      </div>

      <div className="chart-body">
        <ResponsiveContainer width="100%" height={400}>
          <RechartsBarChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
            <XAxis
              dataKey="date"
              tickFormatter={formatXAxis}
              stroke="#7f8c8d"
              style={{ fontSize: '12px' }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              stroke="#7f8c8d"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => formatAxisValue(value, metric_unit)}
            />
            <Tooltip content={<CustomTooltip />} />
            {barKeys.length > 1 && <Legend />}

            {barKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                name={key === 'value' ? metric_name : key}
                fill={colors[index % colors.length]}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

BarChart.propTypes = {
  chartId: PropTypes.string.isRequired,
};

export default BarChart;
